import { type LocationObject } from "expo-location";
import * as Sentry from "@sentry/react-native";

import UserApiBase from "../base/UserApiBase";

import { type UserImageMedia, UserImageType } from "@/schemas/user/image";
import { type OkResponse } from "@/schemas/api/ok";
import { type AppUserSession } from "@/schemas/auth/session";
import { UserAttributeType } from "@/schemas/profile/UserAttributeSet";
import { type UserProfile } from "@/schemas/user/profile";
import { type UserProfilePreview } from "@/schemas/user/preview";
import universalUploadImage from "@/utils/image/universalUploadImage";
import { Coordinates } from "@/schemas/location/cordinate";

export type UserAttributeUpdateEvent = {
  attribute: UserAttributeType;
  values: string[];
};

export enum UserProfileImageUpdateType {
  BACKGROUND = "BACKGROUND",
  AVATAR = "AVATAR",
}

export type UserCoordinateUpdateRequest = {
  coordinates: Coordinates;
};

class ProfileService extends UserApiBase {
  public constructor(session: AppUserSession) {
    super("/profile", session);
  }

  public async getUserProfile(username: string): Promise<UserProfile> {
    return this.get<UserProfile>(`/${username}`);
  }

  public async getUserProfilePreview(
    username: string,
  ): Promise<UserProfilePreview> {
    return this.get<UserProfilePreview>(`/preview/${username}`);
  }

  public async updateProfile(
    type: UserProfileImageUpdateType,
    image: UserImageMedia,
  ): Promise<OkResponse> {
    return this.post(`/image`, {
      imageId: image.id,
      updateType: type,
    });
  }

  public async updateAttributes(
    attribute: UserAttributeType,
    values: string[],
  ): Promise<OkResponse> {
    const payload: UserAttributeUpdateEvent = {
      attribute: attribute,
      values: values,
    };

    try {
      return await this.post(`/attributes`, payload);
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: `Failed to update ${attribute} attributes`,
          values,
        },
      });
      throw error;
    }
  }

  public async registerGPSCoordinates(
    location: LocationObject,
  ): Promise<OkResponse> {
    const payload: UserCoordinateUpdateRequest = {
      coordinates: {
        latitude: location.coords.latitude,
        longitude: location.coords.longitude,
      },
    };
    return this.post(`/register_location`, payload);
  }

  public async replaceUserAvatar(
    onUploadStart: () => void,
    onCancel: () => void,
    onFail: () => void,
    onComplete: (image: UserImageMedia) => void,
  ): Promise<void> {
    const updateProfileAvatar = async (
      image: UserImageMedia,
    ): Promise<void> => {
      const profileService = new ProfileService(this.session);
      await profileService.updateProfile(
        UserProfileImageUpdateType.AVATAR,
        image,
      );
    };

    await universalUploadImage({
      session: this.session,
      imageType: UserImageType.AVATAR,
      onUploadStart,
      onUploadSuccess: async (userImages) => {
        if (userImages && userImages.length > 0 && userImages[0]) {
          const avatarImage = userImages[0];
          await updateProfileAvatar(avatarImage);
          onComplete(avatarImage);
        }
      },
      onUploadError: onFail,
      onPickerCancel: onCancel,
    });
  }
}

export default ProfileService;
